import { useCallback } from 'react';
import type React from 'react';

interface UseMenuKeyboardOptions {
    menuRef: React.RefObject<HTMLDivElement | null>;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement | null>;
    isSubmenu?: boolean;
    onSubmenuClose?: () => void;
    openSubmenuForItem?: (itemId: string | null) => void;
}

function getMenuItems(menuRef: React.RefObject<HTMLDivElement | null>): HTMLElement[] {
    if (!menuRef.current) return [];
    const allItems = menuRef.current.querySelectorAll<HTMLElement>('[data-menu-item]');
    // Only include direct items (whose closest [role="menu"] is this menu)
    return Array.from(allItems).filter((el) => {
        const closestMenu = el.closest('[role="menu"]');
        return closestMenu === menuRef.current;
    });
}

function getEnabledItems(items: HTMLElement[]): HTMLElement[] {
    return items.filter((el) => el.getAttribute('aria-disabled') !== 'true');
}

function focusItem(item: HTMLElement, allItems: HTMLElement[]): void {
    for (const el of allItems) {
        el.setAttribute('tabindex', '-1');
    }
    item.setAttribute('tabindex', '0');
    item.focus();
}

export function useMenuKeyboard({
    menuRef,
    onClose,
    triggerRef,
    isSubmenu,
    onSubmenuClose,
    openSubmenuForItem,
}: UseMenuKeyboardOptions): (e: React.KeyboardEvent) => void {
    return useCallback(
        (e: React.KeyboardEvent) => {
            const items = getMenuItems(menuRef);
            if (items.length === 0) return;

            const enabledItems = getEnabledItems(items);
            const currentIndex = items.indexOf(document.activeElement as HTMLElement);

            switch (e.key) {
                case 'ArrowDown': {
                    e.preventDefault();
                    // Find next enabled item, wrapping
                    let next = currentIndex;
                    for (let i = 0; i < items.length; i++) {
                        next = (next + 1) % items.length;
                        if (enabledItems.includes(items[next])) break;
                    }
                    focusItem(items[next], items);
                    break;
                }
                case 'ArrowUp': {
                    e.preventDefault();
                    let prev = currentIndex === -1 ? 0 : currentIndex;
                    for (let i = 0; i < items.length; i++) {
                        prev = (prev - 1 + items.length) % items.length;
                        if (enabledItems.includes(items[prev])) break;
                    }
                    focusItem(items[prev], items);
                    break;
                }
                case 'Home': {
                    e.preventDefault();
                    if (enabledItems.length > 0) {
                        focusItem(enabledItems[0], items);
                    }
                    break;
                }
                case 'End': {
                    e.preventDefault();
                    if (enabledItems.length > 0) {
                        focusItem(enabledItems[enabledItems.length - 1], items);
                    }
                    break;
                }
                case 'Enter': {
                    e.preventDefault();
                    const focused = document.activeElement as HTMLElement;
                    if (focused && focused.getAttribute('aria-disabled') !== 'true') {
                        focused.click();
                    }
                    break;
                }
                case ' ': {
                    e.preventDefault();
                    const focused = document.activeElement as HTMLElement;
                    if (focused && focused.getAttribute('aria-disabled') !== 'true') {
                        focused.click();
                    }
                    break;
                }
                case 'Escape': {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isSubmenu && onSubmenuClose) {
                        onSubmenuClose();
                    } else {
                        onClose();
                        triggerRef?.current?.focus();
                    }
                    break;
                }
                case 'Tab': {
                    // Don't preventDefault — let focus leave naturally
                    onClose();
                    break;
                }
                case 'ArrowRight': {
                    const focused = document.activeElement as HTMLElement;
                    if (focused && focused.getAttribute('aria-haspopup') === 'menu') {
                        e.preventDefault();
                        const itemId = focused.getAttribute('data-menu-item-id');
                        if (itemId && openSubmenuForItem) {
                            openSubmenuForItem(itemId);
                        }
                    }
                    break;
                }
                case 'ArrowLeft': {
                    if (isSubmenu && onSubmenuClose) {
                        e.preventDefault();
                        onSubmenuClose();
                    }
                    break;
                }
            }
        },
        [menuRef, onClose, triggerRef, isSubmenu, onSubmenuClose, openSubmenuForItem],
    );
}
